import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Send,
  AlertCircle,
  RotateCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { PatientSectionLayout } from './PatientSectionLayout';
import {
  getConversations,
  getConversationMessages,
  getMessageMedia,
  sendDietPlanViaWhatsApp,
  type MessagingMessageRead,
  type MessagingConversationRead,
} from '../../services/messaging/messagingService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { CalendarDatePicker } from '../../components/CalendarDatePicker';
import './PatientMessagesPage.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
];

const STATUS_DISPLAY: Record<
  string,
  { label: string; icon: React.ReactNode; className: string }
> = {
  delivered: {
    label: 'Delivered',
    icon: <CheckCircle size={16} />,
    className: 'message-status--delivered',
  },
  sent: {
    label: 'Sent',
    icon: <Send size={16} />,
    className: 'message-status--sent',
  },
  read: {
    label: 'Read',
    icon: <CheckCircle size={16} />,
    className: 'message-status--read',
  },
  failed: {
    label: 'Failed',
    icon: <AlertCircle size={16} />,
    className: 'message-status--failed',
  },
  queued: {
    label: 'Queued',
    icon: <Send size={16} />,
    className: 'message-status--queued',
  },
};

function getStatusDisplay(status: string) {
  return (
    STATUS_DISPLAY[status] ?? {
      label: status || '—',
      icon: <AlertCircle size={16} />,
      className: 'message-status--unknown',
    }
  );
}

function isUndelivered(status: string): boolean {
  return status !== 'delivered' && status !== 'read';
}

const TEMPLATE_PILL: Record<string, { label: string; className: string }> = {
  diet_plan_ready: { label: 'DIET PLAN', className: 'template-pill--diet' },
};

function getTemplatePill(templateName: string | null) {
  if (!templateName) return null;
  const pill =
    TEMPLATE_PILL[templateName] ?? {
      label: templateName.replace(/_/g, ' ').toUpperCase(),
      className: 'template-pill--default',
    };
  return pill;
}

const PAGE_SIZE = 10;

const PatientMessagesPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const numericPatientId = patientId ? Number(patientId) : NaN;
  const validId = !Number.isNaN(numericPatientId) && numericPatientId > 0;

  const [conversations, setConversations] = useState<MessagingConversationRead[]>([]);
  const [messages, setMessages] = useState<MessagingMessageRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default on load: to = latest (today), from = one month ago
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    return from.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>(() => {
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    return from.toISOString().slice(0, 10);
  });
  const [appliedDateTo, setAppliedDateTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [appliedStatus, setAppliedStatus] = useState<string>('all');
  const [page, setPage] = useState(1);

  const loadMessages = useCallback(async () => {
    if (!validId) return;
    setLoading(true);
    setError(null);
    try {
      const convRes = await getConversations({ patient_user_id: numericPatientId, limit: 10 });
      const convs = convRes.items ?? [];
      setConversations(convs);

      const allMessages: MessagingMessageRead[] = [];
      for (const conv of convs) {
        const list = await getConversationMessages(conv.id, { limit: 200 });
        list.filter((m) => m.direction === 'outbound').forEach((m) => allMessages.push(m));
      }
      allMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setMessages(allMessages);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [numericPatientId, validId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const applyFilters = useCallback(() => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedStatus(statusFilter);
    setPage(1);
  }, [dateFrom, dateTo, statusFilter]);

  const filteredMessages = useMemo(() => {
    const fromTs = appliedDateFrom ? new Date(appliedDateFrom + 'T00:00:00').getTime() : 0;
    const toTs = appliedDateTo ? new Date(appliedDateTo + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;
    return messages.filter((m) => {
      const ts = new Date(m.sent_at || m.created_at).getTime();
      if (ts < fromTs || ts > toTs) return false;
      if (appliedStatus !== 'all' && m.status !== appliedStatus) return false;
      return true;
    });
  }, [messages, appliedDateFrom, appliedDateTo, appliedStatus]);

  const totalFiltered = filteredMessages.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const paginatedMessages = useMemo(
    () => filteredMessages.slice(startIdx, startIdx + PAGE_SIZE),
    [filteredMessages, startIdx]
  );

  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = useCallback(
    async (message: MessagingMessageRead) => {
      // Support both "metadata" (API with response_model_by_alias) and "metadata_" (legacy)
      const meta = message.metadata ?? (message as { metadata_?: { plan_id?: string; version_id?: string } }).metadata_;
      const planId = meta?.plan_id;
      const versionId = meta?.version_id;
      if (
        message.template_name === 'diet_plan_ready' &&
        planId &&
        versionId
      ) {
        setResendError(null);
        setResendLoading(true);
        try {
          await sendDietPlanViaWhatsApp(planId, versionId);
          await loadMessages();
        } catch (e: unknown) {
          const err = e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
            : e instanceof Error ? e.message : 'Failed to resend';
          setResendError(String(err));
        } finally {
          setResendLoading(false);
        }
        return;
      }
      // Old diet-plan messages have no stored plan_id/version_id; open Diet Plans to send again
      navigate(`/patients/${patientId}/diet-plans`);
    },
    [navigate, patientId, loadMessages]
  );

  const handleViewMedia = useCallback(async (messageId: number) => {
    try {
      const { urls } = await getMessageMedia(messageId);
      urls.forEach((url) => window.open(url, '_blank', 'noopener'));
    } catch {
      // ignore
    }
  }, []);

  return (
    <PatientSectionLayout section="messages">
      <section className="dashboard-section patient-messages-section">
        <div className="patient-messages-card">
          <h2 className="patient-messages-title">Messages</h2>
          <p className="patient-messages-subtitle">
            WhatsApp messages sent to this patient. Resend resends the same diet plan for messages that have resend data; for older messages it opens Diet Plans so you can choose a plan to send again.
          </p>

          <div className="patient-messages-filters patient-messages-filters--lucid">
            <div className="patient-messages-filter-field">
              <CalendarDatePicker
                label="FROM DATE"
                id="messages-date-from"
                value={dateFrom}
                onChange={(from) => setDateFrom(from || dateTo)}
                maxDate={dateTo}
                placeholder="Select date"
                dateDisplayFormat="short"
              />
            </div>
            <div className="patient-messages-filter-field">
              <CalendarDatePicker
                label="TO DATE"
                id="messages-date-to"
                value={dateTo}
                onChange={(to) => setDateTo(to || dateFrom)}
                minDate={dateFrom}
                placeholder="Select date"
                dateDisplayFormat="short"
              />
            </div>
            <div className="patient-messages-filter-field">
              <label className="patient-messages-filter-label" htmlFor="messages-status">
                STATUS
              </label>
              <div className="patient-messages-filter-input-wrap patient-messages-filter-input-wrap--status">
                <select
                  id="messages-status"
                  className="patient-messages-status-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="patient-messages-status-chevron" aria-hidden />
              </div>
            </div>
            <div className="patient-messages-filter-actions">
              <button type="button" className="patient-messages-apply-btn" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>

          {(error || resendError) && (
            <div className="patient-messages-error" role="alert">
              {resendError ?? error}
            </div>
          )}

          {loading ? (
            <div className="patient-messages-loading">Loading messages…</div>
          ) : filteredMessages.length === 0 ? (
            <div className="patient-messages-empty">No messages match the selected filters.</div>
          ) : (
            <>
              <div className="patient-messages-table-wrap patient-messages-table-wrap--lucid">
                <table className="patient-messages-table patient-messages-table--lucid">
                  <thead>
                    <tr>
                      <th>DATE SENT</th>
                      <th>TEMPLATE</th>
                      <th>MESSAGE CONTENT</th>
                      <th>RECIPIENT</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMessages.map((m) => {
                      const statusDisplay = getStatusDisplay(m.status);
                      const showResend = m.direction === 'outbound' && isUndelivered(m.status);
                      const content =
                        m.body && m.body.trim()
                          ? m.body
                          : m.template_name
                            ? `Template: ${m.template_name}`
                            : '—';
                      const sentAt = m.sent_at || m.created_at;
                      const pill = getTemplatePill(m.template_name);
                      return (
                        <tr key={m.id}>
                          <td className="patient-messages-cell-date-sent">
                            {sentAt ? (
                              <>
                                <span className="patient-messages-date-sent-date">
                                  {formatDate(sentAt)}
                                </span>
                                <span className="patient-messages-date-sent-time">
                                  {formatTime(sentAt)}
                                </span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="patient-messages-cell-template">
                            {pill ? (
                              <span className={`patient-messages-template-pill ${pill.className}`}>
                                {pill.label}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="patient-messages-cell-content" title={m.body || undefined}>
                            {content}
                            {(m.media_urls?.length ?? 0) > 0 && (
                              <>
                                {' · '}
                                <button
                                  type="button"
                                  className="patient-messages-view-media-btn"
                                  onClick={() => handleViewMedia(m.id)}
                                >
                                  View {m.media_urls!.length} attachment{m.media_urls!.length !== 1 ? 's' : ''}
                                </button>
                              </>
                            )}
                          </td>
                          <td className="patient-messages-cell-recipient">
                            {m.to_phone_e164 ?? '—'}
                          </td>
                          <td>
                            <span className={`patient-messages-status-inline ${statusDisplay.className}`}>
                              {statusDisplay.icon}
                              <span>{statusDisplay.label}</span>
                            </span>
                            {m.error_message && (
                              <span className="patient-messages-error-text" title={m.error_message}>
                                {m.error_message.slice(0, 40)}
                                {m.error_message.length > 40 ? '…' : ''}
                              </span>
                            )}
                          </td>
                          <td className="patient-messages-cell-actions">
                            {showResend ? (
                              <button
                                type="button"
                                className="patient-messages-resend-btn patient-messages-resend-btn--lucid"
                                onClick={() => handleResend(m)}
                                disabled={resendLoading}
                              >
                                <RotateCw size={16} />
                                <span>{resendLoading ? 'Sending…' : 'Resend'}</span>
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="patient-messages-pagination">
                <span className="patient-messages-pagination-summary">
                  Showing {totalFiltered === 0 ? 0 : startIdx + 1} to{' '}
                  {Math.min(startIdx + PAGE_SIZE, totalFiltered)} of {totalFiltered} messages
                </span>
                <div className="patient-messages-pagination-controls">
                  <button
                    type="button"
                    className="patient-messages-pagination-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`patient-messages-pagination-btn patient-messages-pagination-btn--page ${
                        p === currentPage ? 'patient-messages-pagination-btn--current' : ''
                      }`}
                      onClick={() => setPage(p)}
                      aria-label={`Page ${p}`}
                      aria-current={p === currentPage ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="patient-messages-pagination-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </PatientSectionLayout>
  );
};

export default PatientMessagesPage;
